/**
 * roleRouting — the single source of truth for authorization-aware post-auth
 * routing decisions.
 *
 * Fixes the production auth-routing bug where a support agent authenticated
 * successfully but landed on the Access Denied page:
 *   1. Unauthenticated visitor hits "/".
 *   2. RoleBasedRedirect (no cached profile) sent them to /dashboard.
 *   3. RequireAuth stored "/dashboard" in sessionStorage.returnTo, bounced to /login.
 *   4. After login, LoginForm gave returnTo UNCONDITIONAL precedence over the
 *      role-specific landing page → support agent sent to /dashboard.
 *   5. /dashboard excludes support_agent → RequireRole rendered Access Denied.
 *
 * The three places that previously each hard-coded their own role→route logic
 * (RoleBasedRedirect, LoginForm, the Access Denied "Go Back" button) now all
 * call into this module, and a saved returnTo is only honored when it is a safe
 * relative path AND the authenticated user's role is actually allowed to view it.
 *
 * This module is intentionally PURE (no React, no window, no side effects) so it
 * is trivially unit-testable and reusable from every call site.
 *
 * ⚠️ The ROUTE_ROLE_RULES table below MIRRORS the RequireRole allow-lists in
 * router.tsx. It does NOT replace them — RequireRole remains the enforced
 * authorization gate (defense in depth). This table exists so return-path
 * resolution can ask "would this role even be allowed there?" without weakening
 * or duplicating the guard. When you add/change a route's allowedRoles in
 * router.tsx, update the matching entry here. roleRouting.test.ts asserts the
 * key invariants so drift fails loudly.
 */

import { matchPath } from "react-router-dom";

/**
 * Sentinel for routes that any authenticated user may access regardless of role
 * (i.e. routes with NO <RequireRole> wrapper in router.tsx, such as /settings).
 */
export const ANY_AUTHENTICATED = "*" as const;

type RoleList = readonly string[] | typeof ANY_AUTHENTICATED;

interface RouteRoleRule {
  /** Route pattern WITH a leading slash, matched via react-router's matchPath. */
  pattern: string;
  /** Roles allowed (raw, as written in router.tsx) or ANY_AUTHENTICATED. */
  roles: RoleList;
}

/**
 * Normalize a role string the SAME way RequireRole does
 * (lowercase + strip every underscore) so "super_admin" === "superadmin" and
 * "support_admin" === "supportadmin". Keeping this identical to the guard is
 * what guarantees this module never diverges from the enforced authorization.
 */
export function normalizeRole(role: string | null | undefined): string {
  return (role ?? "").toLowerCase().replace(/_/g, "");
}

/**
 * Ordered authorization table mirroring router.tsx. Order matters: matchPath is
 * evaluated one pattern at a time and the FIRST match wins, so concrete paths
 * (e.g. /campaigns/lookups) MUST precede parameterized ones (/campaigns/:id) —
 * exactly as they are ordered in router.tsx.
 */
export const ROUTE_ROLE_RULES: readonly RouteRoleRule[] = [
  // Top-level authenticated standalone route.
  {
    pattern: "/booth-checkin",
    roles: [
      "super_admin",
      "superadmin",
      "admin",
      "manager",
      "regional_manager",
      "territory_manager",
      "staff",
      "msr",
    ],
  },

  // Dashboard-shell children (router.tsx order preserved).
  {
    pattern: "/dashboard",
    roles: [
      "super_admin",
      "admin",
      "staff",
      "manager",
      "regional_manager",
      "territory_manager",
    ],
  },
  {
    pattern: "/partner-dashboard",
    roles: [
      "super_admin",
      "admin",
      "manager",
      "finance",
      "partner",
      "support_admin",
      "support_agent",
    ],
  },
  {
    pattern: "/organizations",
    roles: [
      "super_admin",
      "admin",
      "staff",
      "manager",
      "regional_manager",
      "territory_manager",
      "support_admin",
      "support_agent",
      "readonly",
    ],
  },
  {
    pattern: "/organizations/:id/edit",
    roles: [
      "super_admin",
      "admin",
      "staff",
      "manager",
      "regional_manager",
      "territory_manager",
      "support_admin",
    ],
  },
  {
    pattern: "/organizations/:id/locations/new",
    roles: [
      "super_admin",
      "admin",
      "staff",
      "manager",
      "regional_manager",
      "territory_manager",
      "support_admin",
    ],
  },
  {
    pattern: "/organizations/:id/locations/:locationId/edit",
    roles: [
      "super_admin",
      "admin",
      "staff",
      "manager",
      "regional_manager",
      "territory_manager",
      "support_admin",
    ],
  },
  {
    pattern: "/organizations/:id",
    roles: [
      "super_admin",
      "admin",
      "staff",
      "manager",
      "regional_manager",
      "territory_manager",
      "support_admin",
      "support_agent",
      "readonly",
    ],
  },
  { pattern: "/contacts/attributes", roles: ["super_admin", "admin"] },
  {
    pattern: "/contacts/:id",
    roles: [
      "super_admin",
      "admin",
      "staff",
      "manager",
      "regional_manager",
      "territory_manager",
      "support_admin",
      "support_agent",
      "readonly",
    ],
  },
  {
    pattern: "/contacts",
    roles: [
      "super_admin",
      "admin",
      "staff",
      "manager",
      "regional_manager",
      "territory_manager",
      "support_admin",
      "support_agent",
      "readonly",
    ],
  },
  {
    pattern: "/admin/ad-sync/runs/:runId",
    roles: ["super_admin", "admin", "it"],
  },
  { pattern: "/admin/ad-sync", roles: ["super_admin", "admin", "it"] },
  {
    pattern: "/admin/email-validation-campaign",
    roles: ["super_admin", "admin"],
  },
  {
    pattern: "/email-subscription-groups",
    roles: ["super_admin", "superadmin", "admin", "it", "manager", "staff"],
  },
  {
    pattern: "/admin/repair-generic-ctas",
    roles: ["super_admin", "admin", "it"],
  },
  { pattern: "/admin/sms-retention", roles: ["super_admin", "admin"] },
  { pattern: "/admin/zuora-reconciliation", roles: ["super_admin", "admin"] },
  { pattern: "/admin/zuora-held-welcomes", roles: ["super_admin", "admin"] },
  { pattern: "/admin/promotion-rules", roles: ["super_admin", "admin"] },
  {
    pattern: "/admin/ticket-rewards",
    roles: ["super_admin", "admin", "manager"],
  },
  {
    pattern: "/admin/invitations",
    roles: ["super_admin", "admin", "support_admin"],
  },
  { pattern: "/msr/events/:id", roles: ["msr", "super_admin", "admin"] },
  { pattern: "/msr/member-lookup", roles: ["super_admin", "admin"] },
  { pattern: "/msr/availability", roles: ["msr", "super_admin", "admin"] },
  { pattern: "/msr/sales-history", roles: ["msr", "super_admin", "admin"] },
  { pattern: "/msr/verify-sale", roles: ["msr", "super_admin", "admin"] },
  {
    pattern: "/msr-team",
    roles: [
      "super_admin",
      "admin",
      "manager",
      "regional_manager",
      "territory_manager",
      "staff",
    ],
  },
  { pattern: "/msr", roles: ["msr", "super_admin", "admin"] },
  { pattern: "/segments", roles: ["super_admin", "admin", "staff", "manager"] },
  { pattern: "/suppression-list", roles: ["super_admin", "admin", "manager"] },
  { pattern: "/deduplication", roles: ["super_admin", "admin"] },
  { pattern: "/tasks", roles: ["super_admin", "admin", "staff", "manager"] },
  {
    // Managers removed 2026-09-30 (owner): Support Tasks is not a manager surface.
    pattern: "/support-tasks",
    roles: ["super_admin", "admin", "staff", "support_admin", "support_agent"],
  },
  { pattern: "/event-performance", roles: ["super_admin", "admin"] },
  {
    // Concrete path: must precede /events/:id (matches router.tsx order).
    pattern: "/events/calendar",
    roles: [
      "super_admin",
      "admin",
      "staff",
      "manager",
      "regional_manager",
      "territory_manager",
      "support_admin",
      "support_agent",
    ],
  },
  { pattern: "/rewards/issue", roles: ["super_admin", "admin"] },
  {
    pattern: "/screen-pop",
    roles: [
      "super_admin",
      "admin",
      "staff",
      "manager",
      "support_admin",
      "support_agent",
    ],
  },
  {
    pattern: "/attorney-questions",
    roles: ["super_admin", "admin", "support_admin", "support_agent"],
  },
  {
    pattern: "/admin/attorney-coverage",
    roles: ["super_admin", "admin"],
  },
  {
    pattern: "/sms-review",
    roles: ["super_admin", "admin", "support_admin", "support_agent"],
  },
  {
    pattern: "/whats-new",
    roles: [
      "super_admin",
      "admin",
      "manager",
      "regional_manager",
      "territory_manager",
      "staff",
      "support_admin",
      "support_agent",
      "it",
      "finance",
      "readonly",
      "msr",
    ],
  },
  {
    pattern: "/events/create",
    roles: [
      "super_admin",
      "admin",
      "staff",
      "manager",
      "regional_manager",
      "territory_manager",
    ],
  },
  {
    pattern: "/events/import",
    roles: ["super_admin", "admin", "staff", "manager"],
  },
  {
    pattern: "/events/:id/edit",
    roles: ["super_admin", "admin", "staff", "manager", "support_admin"],
  },
  {
    pattern: "/events/:id",
    roles: [
      "super_admin",
      "admin",
      "staff",
      "manager",
      "regional_manager",
      "territory_manager",
      "support_admin",
      "support_agent",
    ],
  },
  {
    pattern: "/events",
    roles: [
      "super_admin",
      "admin",
      "staff",
      "manager",
      "regional_manager",
      "territory_manager",
      "support_admin",
      "support_agent",
    ],
  },
  {
    pattern: "/interactions",
    roles: [
      "super_admin",
      "admin",
      "staff",
      "manager",
      "support_admin",
      "support_agent",
    ],
  },
  {
    pattern: "/calendar-requests",
    roles: [
      "super_admin",
      "admin",
      "staff",
      "manager",
      "support_admin",
      "support_agent",
    ],
  },
  { pattern: "/campaigns/create", roles: ["super_admin", "admin", "manager"] },
  {
    pattern: "/campaigns/lookups/new",
    roles: ["super_admin", "admin", "manager"],
  },
  {
    pattern: "/campaigns/lookups/:id",
    roles: ["super_admin", "admin", "manager"],
  },
  { pattern: "/campaigns/lookups", roles: ["super_admin", "admin", "manager"] },
  {
    pattern: "/campaigns/:campaignId/flights/:flightId",
    roles: ["super_admin", "admin", "manager"],
  },
  { pattern: "/campaigns/:id", roles: ["super_admin", "admin", "manager"] },
  { pattern: "/campaigns", roles: ["super_admin", "admin", "manager"] },
  { pattern: "/offers", roles: ["super_admin", "admin", "manager"] },
  { pattern: "/sequences/create", roles: ["super_admin", "admin", "manager"] },
  { pattern: "/sequences/triage", roles: ["super_admin", "admin", "manager"] },
  {
    pattern: "/sequences/:id/edit",
    roles: ["super_admin", "admin", "manager"],
  },
  { pattern: "/sequences/:id", roles: ["super_admin", "admin", "manager"] },
  { pattern: "/sequences", roles: ["super_admin", "admin", "manager"] },
  {
    pattern: "/sales-team/create",
    roles: [
      "super_admin",
      "admin",
      "manager",
      "regional_manager",
      "territory_manager",
    ],
  },
  {
    pattern: "/sales-team/:id",
    roles: [
      "super_admin",
      "admin",
      "manager",
      "regional_manager",
      "territory_manager",
      "staff",
    ],
  },
  {
    pattern: "/sales-team",
    roles: [
      "super_admin",
      "admin",
      "manager",
      "regional_manager",
      "territory_manager",
      "staff",
    ],
  },
  {
    pattern: "/reports/events-by-organization",
    roles: [
      "super_admin",
      "admin",
      "manager",
      "regional_manager",
      "territory_manager",
      "staff",
    ],
  },
  {
    pattern: "/reports/event-attendance",
    roles: [
      "super_admin",
      "admin",
      "manager",
      "regional_manager",
      "territory_manager",
      "staff",
    ],
  },
  {
    pattern: "/reports/conversion-summary",
    roles: [
      "super_admin",
      "admin",
      "manager",
      "regional_manager",
      "territory_manager",
      "staff",
    ],
  },
  {
    pattern: "/reports/promoter-fee-reconciliation",
    roles: [
      "super_admin",
      "admin",
      "manager",
      "regional_manager",
      "territory_manager",
      "staff",
    ],
  },
  {
    pattern: "/reports/msr-book-of-business",
    roles: [
      "super_admin",
      "admin",
      "manager",
      "regional_manager",
      "territory_manager",
      "staff",
    ],
  },
  {
    pattern: "/reports/verified-members",
    roles: [
      "super_admin",
      "admin",
      "manager",
      "regional_manager",
      "territory_manager",
    ],
  },
  {
    pattern: "/reports/sales-activity-by-type",
    roles: [
      "super_admin",
      "admin",
      "manager",
      "regional_manager",
      "territory_manager",
      "staff",
    ],
  },
  {
    pattern: "/reports/sales-by-rep",
    roles: [
      "super_admin",
      "admin",
      "manager",
      "regional_manager",
      "territory_manager",
      "staff",
    ],
  },
  {
    pattern: "/reports/phone-sales",
    roles: [
      "super_admin",
      "admin",
      "manager",
      "regional_manager",
      "territory_manager",
    ],
  },
  {
    pattern: "/reports/counter-sales/aggregate",
    roles: [
      "super_admin",
      "admin",
      "manager",
      "regional_manager",
      "territory_manager",
      "staff",
    ],
  },
  {
    pattern: "/reports/counter-sales",
    roles: [
      "super_admin",
      "admin",
      "manager",
      "regional_manager",
      "territory_manager",
    ],
  },
  { pattern: "/reports/sms-retention-held", roles: ["super_admin", "admin"] },
  {
    pattern: "/reports",
    roles: [
      "super_admin",
      "admin",
      "manager",
      "regional_manager",
      "territory_manager",
      "staff",
    ],
  },
  {
    pattern: "/sales-activities/phone-sales/new",
    roles: [
      "super_admin",
      "admin",
      "manager",
      "regional_manager",
      "territory_manager",
      "staff",
    ],
  },
  {
    pattern: "/sales-activities/phone-sales/:id",
    roles: [
      "super_admin",
      "admin",
      "manager",
      "regional_manager",
      "territory_manager",
      "staff",
    ],
  },
  {
    pattern: "/sales-activities/phone-sales",
    roles: [
      "super_admin",
      "admin",
      "manager",
      "regional_manager",
      "territory_manager",
      "staff",
    ],
  },
  {
    pattern: "/commission-estimator",
    roles: [
      "super_admin",
      "admin",
      "manager",
      "regional_manager",
      "territory_manager",
      "staff",
      "msr",
    ],
  },
  {
    pattern: "/account-reviews/create",
    roles: ["super_admin", "admin", "manager", "staff"],
  },
  {
    pattern: "/account-reviews/:id",
    roles: ["super_admin", "admin", "manager", "staff"],
  },
  {
    pattern: "/account-reviews",
    roles: ["super_admin", "admin", "manager", "staff"],
  },
  {
    pattern: "/sales-calendar",
    roles: [
      "super_admin",
      "admin",
      "manager",
      "regional_manager",
      "territory_manager",
      "staff",
    ],
  },
  {
    pattern: "/partners/new",
    roles: [
      "super_admin",
      "admin",
      "manager",
      "regional_manager",
      "territory_manager",
    ],
  },
  {
    pattern: "/partners/:id",
    roles: [
      "super_admin",
      "admin",
      "manager",
      "regional_manager",
      "territory_manager",
    ],
  },
  {
    pattern: "/partners",
    roles: [
      "super_admin",
      "admin",
      "manager",
      "regional_manager",
      "territory_manager",
    ],
  },
  { pattern: "/content", roles: ["super_admin", "admin", "manager", "staff"] },
  {
    pattern: "/landing-pages/create",
    roles: ["super_admin", "admin", "manager"],
  },
  { pattern: "/landing-pages/:id", roles: ["super_admin", "admin", "manager"] },
  { pattern: "/landing-pages", roles: ["super_admin", "admin", "manager"] },
  {
    pattern: "/redemptions",
    roles: [
      "super_admin",
      "admin",
      "manager",
      "regional_manager",
      "territory_manager",
      "finance",
      "partner",
      "support_admin",
      "support_agent",
    ],
  },
  { pattern: "/voucher-templates", roles: ["super_admin", "admin"] },
  {
    pattern: "/partner-offers/:id",
    roles: ["super_admin", "admin", "manager"],
  },
  { pattern: "/partner-offers", roles: ["super_admin", "admin", "manager"] },
  { pattern: "/form-handlers/new", roles: ["super_admin", "admin"] },
  { pattern: "/form-handlers/:id/edit", roles: ["super_admin", "admin"] },
  {
    pattern: "/form-handlers/:id/submissions",
    roles: ["super_admin", "admin"],
  },
  { pattern: "/form-handlers", roles: ["super_admin", "admin"] },
  // /settings has NO RequireRole wrapper in router.tsx → any authenticated user.
  { pattern: "/settings", roles: ANY_AUTHENTICATED },
  { pattern: "/error-logs", roles: ["super_admin"] },
];

/**
 * Role → default landing route. This is the ONE mapping used by
 * RoleBasedRedirect, LoginForm, and the Access Denied "Go Back" button.
 *
 * EVERY value of the UserRole enum (auth.types.ts) has an EXPLICIT case here,
 * and each destination is a route that role is actually allowed to load per the
 * RequireRole allow-lists in router.tsx (mirrored in ROUTE_ROLE_RULES above).
 * A role must NEVER default to a page its own RequireRole would refuse — that is
 * exactly the support-agent → /dashboard → Access Denied bug this module fixes,
 * and it applies equally to concierge (which no route grants) and to
 * any unknown/null role.
 *
 *   super_admin / admin / manager / staff → /dashboard  (in the /dashboard allow-list)
 *   support_admin / support_agent         → /support-tasks
 *   partner / finance                     → /redemptions
 *   it                                    → /settings
 *   msr                                   → /msr
 *   readonly                              → /contacts  (owner 2026-11-03: view-only contacts + organizations)
 *   concierge                             → /settings  (no route grants it a home)
 *   unknown / null / undefined            → /settings  (genuinely always-loadable)
 *
 * /settings is the ONLY authenticated route with NO <RequireRole> wrapper in
 * router.tsx (ANY_AUTHENTICATED above), so it is the one destination guaranteed
 * to render for any signed-in user regardless of role. It is therefore the safe
 * fallback for roles with no authorized home and for unrecognized roles — never a
 * lockout, and it does NOT widen access (any authenticated user can already load it).
 */
export function getDefaultRouteForRole(
  role: string | null | undefined,
): string {
  switch (normalizeRole(role)) {
    case "superadmin":
    case "admin":
    case "manager":
    case "regionalmanager":
    case "territorymanager":
    case "staff":
      return "/dashboard";
    case "supportagent":
    case "supportadmin":
      return "/support-tasks";
    case "partner":
    case "finance":
      return "/redemptions";
    case "it":
      return "/settings";
    case "msr":
      return "/msr";
    case "attorney":
      // The attorney PORTAL is a later slice; no route admits this role yet. Land
      // them on the any-authenticated /settings rather than relying on the unknown-
      // role default, so the day a portal exists this line is the one to change.
      return "/settings";
    case "readonly":
      // Read-only viewer (owner 2026-11-03): contacts (membership lookups) is its home.
      return "/contacts";
    case "concierge":
      // No RequireRole allow-list in router.tsx grants these roles any page, so
      // there is no role-specific home. Land them on the any-authenticated
      // /settings rather than /dashboard (which would Access-Deny them).
      return "/settings";
    default:
      // Unknown / null / undefined role: fail SAFE to the any-authenticated
      // route so an unrecognized role is never bounced to Access Denied.
      return "/settings";
  }
}

/** Extract the pathname portion of a relative path (drop ?query and #hash). */
function extractPathname(path: string): string {
  const queryIdx = path.indexOf("?");
  const hashIdx = path.indexOf("#");
  let end = path.length;
  if (queryIdx !== -1) {
    end = Math.min(end, queryIdx);
  }
  if (hashIdx !== -1) {
    end = Math.min(end, hashIdx);
  }
  return path.slice(0, end);
}

/**
 * Is `path` a safe, same-origin RELATIVE path we may navigate to after login?
 * Rejects absolute URLs, protocol-relative ("//evil.example"), backslash tricks
 * ("/\\evil.example" — browsers treat "\" as "/"), whitespace/control chars, and
 * the /login page itself (avoids a redirect loop back to login). Hyphens are
 * allowed — many real routes are hyphenated (/support-tasks, /qr-codes).
 */
export function isSafeRelativePath(path: string | null | undefined): boolean {
  if (typeof path !== "string" || path.length === 0) {
    return false;
  }
  // Must be root-relative.
  if (!path.startsWith("/")) {
    return false;
  }
  // Reject any backslash outright (browsers normalize "\" → "/", so "/\\host"
  // and "\\\\host" smuggle absolute/protocol-relative targets).
  if (path.includes("\\")) {
    return false;
  }
  // Protocol-relative or percent-encoded-slash smuggled absolute URL.
  if (
    path.startsWith("//") ||
    path.startsWith("/%2f") ||
    path.startsWith("/%2F") ||
    path.startsWith("/%5c") ||
    path.startsWith("/%5C")
  ) {
    return false;
  }
  // Reject ASCII control chars + space ( - ). Anything above space
  // (incl. hyphen, slash, alphanum) is fine.
  for (let i = 0; i < path.length; i += 1) {
    if (path.charCodeAt(i) <= 0x20) {
      return false;
    }
  }
  // Don't bounce back to the login page.
  if (extractPathname(path) === "/login") {
    return false;
  }
  return true;
}

/**
 * Would `role` be permitted to view `path` per the (mirrored) route authorization
 * table? Query strings and hashes are ignored — only the pathname is matched.
 *
 * Returns false for unknown/unlisted paths so an unrecognized returnTo is
 * discarded in favor of the role default (fail-closed).
 */
export function isRouteAllowedForRole(
  path: string | null | undefined,
  role: string | null | undefined,
): boolean {
  if (typeof path !== "string" || path.length === 0) {
    return false;
  }
  const normalizedRole = normalizeRole(role);
  if (!normalizedRole) {
    return false;
  }
  const pathname = extractPathname(path);
  if (!pathname.startsWith("/")) {
    return false;
  }

  for (const rule of ROUTE_ROLE_RULES) {
    if (matchPath({ path: rule.pattern, end: true }, pathname)) {
      if (rule.roles === ANY_AUTHENTICATED) {
        return true;
      }
      return rule.roles.some((r) => normalizeRole(r) === normalizedRole);
    }
  }
  // Unlisted path → fail closed.
  return false;
}

/**
 * Resolve where an authenticated user should land after login.
 *
 * Honors `returnTo` ONLY when it is a safe relative path AND the user's role is
 * allowed to access it; otherwise returns the role default. Query strings on
 * authorized internal paths are preserved.
 */
export function resolvePostLoginRoute(
  returnTo: string | null | undefined,
  role: string | null | undefined,
): string {
  if (
    returnTo &&
    isSafeRelativePath(returnTo) &&
    isRouteAllowedForRole(returnTo, role)
  ) {
    return returnTo;
  }
  return getDefaultRouteForRole(role);
}
